import {useState} from "react";
import styles from "./DealsCopyPlan.module.css";

// У2 (23.08.2026) — списъкът, който се одобрява ПРЕДИ копирането на замени.
//
// Защо изобщо го има: една замяна се записва наведнъж за двамата участници,
// затова щом е копирана при „А", тя вече стои и при „Б". Досега при „Б" се
// копираше пак и се удвояваше. Сега вече отразените се разпознават и се
// показват сиви — човекът вижда кои са и копира само останалите.
//
// Геодезистът избра точно този вариант (писмо от 23.08.2026) и с довод, който
// личи в реда на колоните: гледа насрещния ползвател. Случаят му е „Б има
// замени с А, Х, М и О; с А вече е отразена, копирам другите три".

// Х1 (26.08.2026): замяна, на която ДАВАЩИЯТ няма откъде да я даде тази година,
// НЕ тръгва отметната и не се води „нова". Съобщено от геодезиста: при бърза
// работа такава замяна влизаше с едно натискане и количеството се появяваше при
// взимащия, без да има откъде да излезе. Отмятането си остава възможно — човекът
// може да знае нещо, което базата още не знае.
const НАДПИС = {
    new: "нова",
    exists: "вече е отразена",
    similar: "подобна",
    missing_company: "няма го за годината",
};

const ЦВЯТ = {
    new: "var(--app-info-fg)",
    exists: "var(--app-muted)",
    similar: "darkorange",
    missing_company: "var(--app-danger-fg)",
};

// Може ли редът изобщо да се копира. Вече отразената — не (това е задачата);
// без насрещен ползвател — не (замяната няма как да се създаде).
const избираем = (i) => i.status === 'new' || i.status === 'similar';

// Какво пише в колоната „Състояние".
const състояние = (i) => (i.no_stock ? "няма налично до момента" : НАДПИС[i.status]);

const цвят = (i) => (i.no_stock ? "var(--app-danger-fg)" : ЦВЯТ[i.status]);

export const DealsCopyPlan = ({plan, year, loading, onCopy, onCancel}) => {
    const [избрани, setИзбрани] = useState(
        () => new Set(plan.items
            .filter(i => i.status === 'new' && !i.no_stock)
            .map(i => i.deal_id)));

    const превключи = (deal_id) => setИзбрани(старо => {
        const ново = new Set(старо);
        ново.has(deal_id) ? ново.delete(deal_id) : ново.add(deal_id);
        return ново;
    });

    const c = plan.counts || {};
    const брой = избрани.size;
    const безНаличност = plan.items.filter(i => i.no_stock && избираем(i)).length;

    return (
        <div className="card mb-3">
            <div className={`card-body ${styles.plan}`}>
                <h6 className="card-title">
                    Замени от {plan.prev_year}: {plan.items.length}
                </h6>
                <p style={{fontSize: "14px", color: "var(--app-muted)"}}>
                    {c.new || 0} нови · {c.exists || 0} вече отразени
                    {(c.similar || 0) > 0 && ` · ${c.similar} подобни`}
                    {(c.missing_company || 0) > 0 && ` · ${c.missing_company} без насрещен ползвател за ${year}`}
                    {безНаличност > 0 && ` · ${безНаличност} без налична площ у даващия`}
                    <br/>
                    Отметнати са само новите. Вече отразените не могат да се
                    отметнат — те вече са вписани за {year} и повторно копиране
                    би ги удвоило.
                    {безНаличност > 0 && (
                        <>
                            <br/>
                            Замяна, на която <strong>даващият няма налична площ</strong> за
                            {' '}{year}, не е отметната — количеството няма откъде да
                            излезе. Можете да я отметнете сами, ако знаете, че площта
                            предстои да влезе.
                        </>
                    )}
                </p>

                <table className="table table-sm align-middle">
                    <thead>
                    <tr>
                        <th scope="col" style={{width: "1%"}}></th>
                        <th scope="col">Насрещен ползвател</th>
                        <th scope="col">Землища</th>
                        <th scope="col" className="text-end">Площ</th>
                        <th scope="col">Състояние</th>
                    </tr>
                    </thead>
                    <tbody>
                    {plan.items.map(i => (
                        <tr key={i.deal_id}
                            style={избираем(i) ? undefined : {color: "var(--app-muted)"}}>
                            <td>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    disabled={!избираем(i) || loading}
                                    checked={избрани.has(i.deal_id)}
                                    onChange={() => превключи(i.deal_id)}
                                    title={избираем(i) ? undefined
                                        : (i.status === 'exists'
                                            ? "Тази замяна вече е вписана за " + year
                                            : "Насрещният ползвател няма данни за " + year)}
                                />
                            </td>
                            <td>
                                <span style={{color: "var(--app-muted)"}}>
                                    {i.direction === 'give' ? 'ДАВА към ' : 'ВЗЕМА от '}
                                </span>
                                {i.other_name}
                            </td>
                            <td style={{fontSize: "13px"}}>
                                {i.settlement_giving_name} → {i.settlement_receiving_name}
                            </td>
                            <td className="text-end">{i.amount.toFixed(3)}</td>
                            <td style={{color: цвят(i)}}
                                title={i.available !== null && i.available !== undefined
                                    ? `Налично у даващия: ${i.available.toFixed(2)}`
                                    : undefined}>
                                {състояние(i)}
                                {i.no_stock && i.available !== null
                                    && i.available !== undefined && (
                                    <span style={{fontSize: "13px"}}>
                                        {' '}(налично {i.available.toFixed(2)})
                                    </span>
                                )}
                                {!i.no_stock && i.status === 'similar' && (
                                    <span style={{fontSize: "13px"}}>
                                        {' '}— вече има същата замяна с{' '}
                                        {i.existing_amount.toFixed(3)}
                                    </span>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                <div className="d-flex gap-2">
                    <button className="btn btn-primary" disabled={loading || брой === 0}
                            onClick={() => onCopy([...избрани])}>
                        {loading ? 'Копиране...' : `Копирай отметнатите (${брой})`}
                    </button>
                    <button className="btn btn-outline-secondary" disabled={loading}
                            onClick={onCancel}>
                        Откажи
                    </button>
                </div>
            </div>
        </div>
    );
};
